import React, { Fragment, useContext, useRef } from 'react';
import PropTypes from 'prop-types';
import Box from '@nubank/nuds-web/components/Box/Box';

import { typeValidation, rendererComponents } from '../../../utils';
import Section from '../Section';
import Column from '../Column';
import ColumnTitle from '../ColumnTitle';
import ColumnLink from '../ColumnLink';
import { MenuContext } from '../../../MenuContext';

import NavigationListLink from './NavigationListLink';
import NavigationListLargeScreenWrapper from './styles/NavigationListLargeScreenWrapper';

const KeysToComponentLinkMap = {
  // eslint-disable-next-line react/prop-types
  Section: ({ children, idNav }) => <Fragment key={idNav}>{children}</Fragment>,
  SectionLink: props => (
    <NavigationListLink
      role="menuitem"
      tabindex="0"
      activeClassName="menu-navigation-link--large-screen__active"
      {...props}
    />
  ),
};

function ColumnLinkCustom({ afterComponent, beforeComponent, ...props }) {
  const AfterComponent = afterComponent;
  const BeforeComponent = beforeComponent;

  return (
    <Box
      tag="li"
      role="none"
      marginBottom="5x"
      display="flex"
      alignItems="center"
    >
      {beforeComponent && <BeforeComponent />}
      <ColumnLink
        role="menuitem"
        tabindex="-1"
        {...props}
      />
      {afterComponent && <AfterComponent />}
    </Box>
  );
}

ColumnLinkCustom.defaultProps = {
  afterComponent: undefined,
  beforeComponent: undefined,
};

ColumnLinkCustom.propTypes = {
  afterComponent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  beforeComponent: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
};

function NavigationListLargeScreen({ componentsList }) {
  const navigationListWrapperRef = useRef(null);
  const { isWhiteColorHero, isHovered, userScrolledBelowTheHero } = useContext(MenuContext);

  const KeysToComponentTabsMap = {
    Section: props => (
      <Section
        role="menu"
        ariaHidden="true"
        className="menu-navigation-list-item-submenu"
        width="auto"
        left="undefined"
        marginLeft="-4x"
        paddingHorizontal="8x"
        {...props}
      />
    ),
    Column: props => <Column tag="ul" {...props} />,
    ColumnTitle: props => (
      <li role="none">
        <ColumnTitle {...props} />
      </li>
    ),
    ColumnLink: ColumnLinkCustom,
  };

  return (
    <NavigationListLargeScreenWrapper
      ref={navigationListWrapperRef}
      tag="nav"
      role="menubar"
      selectedColor={isWhiteColorHero || isHovered || userScrolledBelowTheHero ? 'black' : 'white'}
      inactiveColor={isWhiteColorHero || isHovered || userScrolledBelowTheHero ? 'rgb(17 17 17 / 60%)' : 'rgb(254 254 254 / 70%)'}
    >
      {componentsList.map(component => (
        <Fragment key={`nav-large-screen-item-${component.props.idNav}`}>
          {component.props.children.length === 1 ? (
            <li
              key={`navigation-list-item-${component.props.idNav}`}
              role="none"
            >
              {rendererComponents(KeysToComponentLinkMap, component.props)}
            </li>
          ) : (
            <li
              role="none"
              key={`navigation-list-item-${component.props.idNav}`}
            >
              <NavigationListLink
                role="menuitem"
                aria-haspopup="true"
                aria-expanded="true"
                tabindex="0"
                iconProps={{
                  name: 'chevron-down',
                  title: ' ', // workaround for accessibility - remove title
                }}
                sectionId={component.props.idNav}
                pointerEventsNone
                {...(({ __TYPE, ...rest }) => rest)(component.props.children[0].props)}
              />
              {rendererComponents(KeysToComponentTabsMap, component.props)}
            </li>
          )}
        </Fragment>
      ))}
    </NavigationListLargeScreenWrapper>
  );
}

NavigationListLargeScreen.propTypes = {
  __TYPE: typeValidation('NavigationListLargeScreen'),
  componentsList: PropTypes.arrayOf(
    PropTypes.shape({
      props: PropTypes.shape({
        __TYPE: PropTypes.string,
        children: PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.object,
          PropTypes.array,
        ]).isRequired,
        name: PropTypes.string,
      }).isRequired,
    }),
  ).isRequired,
};

NavigationListLargeScreen.defaultProps = {
  __TYPE: 'NavigationListLargeScreen',
};

export default React.memo(NavigationListLargeScreen);
